import React, { useState, useEffect } from 'react';

const StyleForm = ({ style, onSubmit, onCancel }) => {
  const isEditMode = !!style;
  
  // État du formulaire
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    image_url: '',
    prompt: '',
    token_cost: 1,
    is_active: true,
    format: 'square'  // Valeur par défaut: carré
  });
  
  // État des messages d'erreur
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Charger les données du style si on est en mode édition
  useEffect(() => {
    if (style) {
      setFormData({
        id: style.id,
        name: style.name || '',
        category: style.category || '',
        description: style.description || '',
        image_url: style.image_url || '',
        prompt: style.prompt || '',
        token_cost: style.token_cost || 1,
        is_active: style.is_active === undefined ? true : style.is_active,
        format: style.format || 'square'  // Utiliser 'square' si non défini
      });
    }
  }, [style]);

  // Gérer les changements de champs
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Effacer l'erreur sur ce champ lorsqu'il est modifié
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Validation du formulaire
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (!formData.category.trim()) {
      newErrors.category = 'La catégorie est requise';
    }
    
    if (!formData.prompt.trim()) {
      newErrors.prompt = 'Le prompt est requis';
    }
    
    if (formData.token_cost < 1) {
      newErrors.token_cost = 'Le coût doit être au moins de 1';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setSubmitError('');
    
    try {
      const result = await onSubmit(formData);
      if (!result.success) {
        setSubmitError(result.error || 'Une erreur est survenue');
      }
    } catch (error) {
      console.error('Erreur de soumission:', error);
      setSubmitError('Erreur lors de la communication avec le serveur');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-content">
      <h2>{isEditMode ? 'Modifier le style' : 'Ajouter un style'}</h2>
      
      {submitError && (
        <div className="panel" style={{ borderColor: 'var(--color-error)', marginBottom: '20px' }}>
          {submitError}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        {/* Nom du style */}
        <div className="form-group">
          <label className="form-label" htmlFor="name">Nom <span className="form-required">*</span></label>
          <input
            type="text"
            id="name"
            name="name"
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            value={formData.name}
            onChange={handleChange}
            placeholder="Nom du style"
            required
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>
        
        {/* Catégorie */}
        <div className="form-group">
          <label className="form-label" htmlFor="category">Catégorie <span className="form-required">*</span></label>
          <input
            type="text"
            id="category"
            name="category"
            className={`form-control ${errors.category ? 'is-invalid' : ''}`}
            value={formData.category}
            onChange={handleChange}
            placeholder="Catégorie du style"
            required
          />
          {errors.category && <div className="invalid-feedback">{errors.category}</div>}
        </div>
        
        {/* Description */}
        <div className="form-group">
          <label className="form-label" htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            className="form-control"
            value={formData.description}
            onChange={handleChange}
            placeholder="Description du style"
            rows={3}
          />
        </div>
        
        {/* URL de l'image */}
        <div className="form-group">
          <label className="form-label" htmlFor="image_url">URL de l'image</label>
          <input
            type="text"
            id="image_url"
            name="image_url"
            className="form-control"
            value={formData.image_url}
            onChange={handleChange}
            placeholder="URL de l'image représentative"
          />
          {formData.image_url && (
            <div className="mt-2" style={{ maxWidth: '200px' }}>
              <img 
                src={formData.image_url} 
                alt="Aperçu" 
                style={{ maxWidth: '100%', borderRadius: 'var(--border-radius-md)' }}
                onError={(e) => e.target.style.display = 'none'}
              />
            </div>
          )}
        </div>
        
        {/* Prompt */}
        <div className="form-group">
          <label className="form-label" htmlFor="prompt">Prompt <span className="form-required">*</span></label>
          <textarea
            id="prompt"
            name="prompt"
            className={`form-control ${errors.prompt ? 'is-invalid' : ''}`}
            value={formData.prompt}
            onChange={handleChange}
            placeholder="Prompt à utiliser pour générer ce style"
            rows={5}
            required
          />
          {errors.prompt && <div className="invalid-feedback">{errors.prompt}</div>}
        </div>
        
        {/* Coût en tokens */}
        <div className="form-group">
          <label className="form-label" htmlFor="token_cost">Coût en tokens <span className="form-required">*</span></label>
          <input
            type="number"
            id="token_cost"
            name="token_cost"
            className={`form-control ${errors.token_cost ? 'is-invalid' : ''}`}
            value={formData.token_cost}
            onChange={handleChange}
            min={1}
            required
          />
          {errors.token_cost && <div className="invalid-feedback">{errors.token_cost}</div>}
        </div>
        
        {/* Format de l'image */}
        <div className="form-group">
          <label className="form-label" htmlFor="format">Format d'image</label>
          <select
            id="format"
            name="format"
            className="form-control"
            value={formData.format}
            onChange={handleChange}
          >
            <option value="square">Carré (1024x1024)</option>
            <option value="portrait">Portrait (1024x1536)</option>
          </select>
          <small className="form-text">
            Choisissez le format de génération d'image pour ce style
          </small>
        </div>
        
        {/* Style actif */}
        <div className="form-group">
          <div className="custom-checkbox">
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleChange}
            />
            <span className="checkmark"></span>
            <label className="form-label" htmlFor="is_active">Style actif</label>
          </div>
        </div>
        
        {/* Boutons de soumission */}
        <div className="mt-4" style={{ display: 'flex', gap: '12px' }}>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? 'En cours...' : (isEditMode ? 'Mettre à jour' : 'Ajouter')}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={onCancel}
            disabled={submitting}
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

export default StyleForm; 